// The athlete's health card (Κάρτα Υγείας Αθλητή, ν. 4479/2017) of a player, to print and take to the doctor:
// an A4 page with the logo of the league, the fields of the card filled in where the site knows them, the
// photo of the player for the doctor's stamp, and the doctor's statement with room for the date and the stamp
import QRCode from 'qrcode'
import { Capacitor, CapacitorHttp } from '@capacitor/core'
import { loadImage, toDataUrl } from '@/utils/matchImage'

const WIDTH = 1240
const HEIGHT = 1754
const FONT = 'Arial, Helvetica, sans-serif'
const INK = '#111111'

function line(ctx, x1, y, x2) {
    ctx.save()
    ctx.strokeStyle = '#555555'
    ctx.lineWidth = 2
    ctx.setLineDash([3, 7])
    ctx.beginPath()
    ctx.moveTo(x1, y)
    ctx.lineTo(x2, y)
    ctx.stroke()
    ctx.restore()
}

function text(ctx, value, x, y, { size = 30, weight = 400, align = 'left', color = INK, maxWidth } = {}) {
    ctx.font = `${weight} ${size}px ${FONT}`
    ctx.textAlign = align
    ctx.fillStyle = color
    ctx.fillText(String(value), x, y, maxWidth)
}

// the words of a text in lines of maxWidth
function wrap(ctx, value, maxWidth) {
    const lines = []
    let current = ''
    String(value).split(' ').forEach(word => {
        const next = current ? `${current} ${word}` : word
        if (current && ctx.measureText(next).width > maxWidth) {
            lines.push(current)
            current = word
        } else {
            current = next
        }
    })
    if (current) lines.push(current)
    return lines
}

function drawImage(ctx, image, x, y, width, height, cover) {
    const scale = cover
        ? Math.max(width / image.width, height / image.height)
        : Math.min(width / image.width, height / image.height)
    const drawWidth = image.width * scale
    const drawHeight = image.height * scale
    ctx.drawImage(image, x + (width - drawWidth) / 2, y + (height - drawHeight) / 2, drawWidth, drawHeight)
}

// an image of the storage straight from it: drawable only if the storage sends CORS headers
function loadShared(src) {
    if (!src) return Promise.resolve(null)
    return new Promise(resolve => {
        const image = new Image()
        image.crossOrigin = 'anonymous'
        image.onload = () => resolve(image)
        image.onerror = () => resolve(null)
        image.src = src
    })
}

// in the app: the image through the native http of the phone, which has no CORS; null elsewhere or when it fails
function loadNative(src) {
    if (!src || !Capacitor.isNativePlatform()) return Promise.resolve(null)
    return CapacitorHttp.get({ url: src, responseType: 'blob' })
        .then(resp => {
            if (resp.status < 200 || resp.status >= 300 || typeof resp.data !== 'string') return null
            const header = Object.keys(resp.headers || {}).find(key => key.toLowerCase() === 'content-type')
            const type = (header && resp.headers[header].split(';')[0]) || 'image/png'
            return loadShared(`data:${type};base64,${resp.data}`)
        })
        .catch(() => null)
}

// card: {league, logo, accent, photo (url), fields: [{label, value, side (beside the photo)}], site,
//        qr (the link of the player: scanned by the stats app of the CMS, it puts the player in the game roster),
//        teamLogo (url, next to the team: the field with team: true)}
// resolves to {blob, overlays}: the images that could not be drawn (photo, team logo), each with its box in % of
// the page, for the page to lay them over the card instead (an <img> shows them without the CORS a canvas needs)
export async function renderHealthCard(card) {
    const load = src => loadImage(src).then(image => image || loadShared(src)).then(image => image || loadNative(src))
    const [logo, photo, teamLogo] = await Promise.all([loadImage(card.logo), load(card.photo), load(card.teamLogo)])
    const overlays = []
    const overlay = (src, x, y, width, height, fit) => overlays.push({
        src, fit, left: x / WIDTH * 100, top: y / HEIGHT * 100, width: width / WIDTH * 100, height: height / HEIGHT * 100
    })
    const element = document.createElement('canvas')
    element.width = WIDTH
    element.height = HEIGHT
    const ctx = element.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, WIDTH, HEIGHT)

    // the frame of the card
    ctx.strokeStyle = INK
    ctx.lineWidth = 4
    ctx.strokeRect(70, 70, WIDTH - 140, HEIGHT - 140)

    // head: the logo and the name of the league, the QR of the player on the right
    let textLeft = 110
    if (logo) {
        const height = 150
        const width = Math.min(380, height * logo.width / logo.height)
        drawImage(ctx, logo, 110, 105, width, height)
        textLeft = 110 + width + 30
    }
    text(ctx, card.league, textLeft, 170, { size: 34, weight: 700, color: card.accent, maxWidth: WIDTH - 340 - textLeft })
    text(ctx, 'Καλαθοσφαίριση', textLeft, 215, { size: 28, color: '#555555' })
    if (card.qr) {
        const qr = document.createElement('canvas')
        await QRCode.toCanvas(qr, card.qr, { width: 160, margin: 1, errorCorrectionLevel: 'M' })
        ctx.drawImage(qr, WIDTH - 110 - 160, 92, 160, 160)
        text(ctx, 'Σκανάρισμα για το ρόστερ', WIDTH - 110 - 80, 276, { size: 17, align: 'center', color: '#555555' })
    }
    ctx.fillStyle = card.accent
    ctx.fillRect(110, 290, WIDTH - 220, 6)

    text(ctx, 'ΚΑΡΤΑ ΥΓΕΙΑΣ ΑΘΛΗΤΗ', WIDTH / 2, 365, { size: 62, weight: 700, align: 'center' })
    text(ctx, 'Νόμου 4479/2017', WIDTH / 2, 412, { size: 30, align: 'center' })

    // the photo, for the stamp of the doctor: all of it in the frame, inside its border
    const box = { x: 130, y: 460, width: 280, height: 350 }
    if (photo) drawImage(ctx, photo, box.x + 2, box.y + 2, box.width - 4, box.height - 4)
    ctx.strokeStyle = INK
    ctx.lineWidth = 3
    ctx.strokeRect(box.x, box.y, box.width, box.height)
    if (!photo && !card.photo) {
        text(ctx, 'Φωτογραφία', box.x + box.width / 2, box.y + box.height / 2, { size: 28, align: 'center', color: '#777777' })
    }
    text(ctx, 'Σφραγίδα ιατρού', box.x + box.width / 2, box.y + box.height + 30, { size: 21, align: 'center', color: '#555555' })
    text(ctx, 'επάνω στη φωτογραφία', box.x + box.width / 2, box.y + box.height + 56, { size: 21, align: 'center', color: '#555555' })

    // a field: its value on a dotted line, the line alone to fill in by hand
    const field = (item, x, y, right) => {
        text(ctx, `${item.label}:`, x, y, { size: 28, weight: 700 })
        ctx.font = `700 28px ${FONT}`
        const start = x + ctx.measureText(`${item.label}:`).width + 18
        line(ctx, start, y + 8, right)
        let valueLeft = start + 8
        // the logo of the team before its name
        if (item.team && card.teamLogo) {
            const size = 46
            if (teamLogo) drawImage(ctx, teamLogo, valueLeft, y - 38, size, size)
            else overlay(card.teamLogo, valueLeft, y - 38, size, size, 'contain')
            valueLeft += size + 12
        }
        if (item.value) text(ctx, item.value, valueLeft, y - 4, { size: 30, maxWidth: right - valueLeft - 10 })
    }
    // beside the photo: the sport, then the fields marked side
    const left = box.x + box.width + 60
    text(ctx, 'Για το άθλημα:', left, 520, { size: 30 })
    text(ctx, 'ΚΑΛΑΘΟΣΦΑΙΡΙΣΗ', left, 566, { size: 34, weight: 700 })
    let y = 640
    card.fields.filter(item => item.side).forEach(item => {
        field(item, left, y, WIDTH - 130)
        y += 66
    })
    y = 925
    card.fields.filter(item => !item.side).forEach(item => {
        field(item, 130, y, WIDTH - 130)
        y += 68
    })

    // the statement of the doctor
    y += 26
    ctx.font = `400 27px ${FONT}`
    wrap(ctx, 'Ο/Η κάτωθι ιατρός πιστοποιεί πως ο/η ανωτέρω επιτρέπεται να συμμετέχει στο παραπάνω άθλημα (προπονήσεις και αγώνες) και έχει υποβληθεί στις προβλεπόμενες ιατρικές εξετάσεις (ΦΕΚ 3254/Β/8-8-2018).', WIDTH - 260)
        .forEach(row => {
            text(ctx, row, 130, y, { size: 27 })
            y += 38
        })
    y += 30
    text(ctx, 'Ημερομηνία:  ........ / ........ / ............', 130, y, { size: 28 })
    y += 80
    text(ctx, 'Ονοματεπώνυμο και σφραγίδα ιατρού', 130, y, { size: 27 })
    text(ctx, '(υπογραφή και επάνω στη φωτογραφία):', 130, y + 36, { size: 27 })
    line(ctx, 130, y + 104, 720)
    // the circle for the stamp
    ctx.save()
    ctx.strokeStyle = '#2b5fae'
    ctx.lineWidth = 3
    ctx.beginPath()
    ctx.arc(WIDTH - 250, y + 30, 100, 0, Math.PI * 2)
    ctx.stroke()
    ctx.restore()

    text(ctx, card.site, WIDTH / 2, HEIGHT - 95, { size: 22, align: 'center', color: '#888888' })

    // the photo it could not draw goes in the frame, inside its border
    if (card.photo && !photo) overlay(card.photo, box.x + 2, box.y + 2, box.width - 4, box.height - 4, 'contain')
    const blob = await new Promise((resolve, reject) => {
        element.toBlob(value => (value ? resolve(value) : reject(new Error('The card could not be made'))), 'image/png')
    })
    return { blob, overlays }
}

// the card (a data: url of it) alone on an A4 page, as a pdf: for the app, where the page cannot print
export async function cardPdf(imageUrl) {
    const image = await loadImage(imageUrl)
    const element = document.createElement('canvas')
    element.width = image.width
    element.height = image.height
    const ctx = element.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, element.width, element.height)
    ctx.drawImage(image, 0, 0)
    const jpeg = await new Promise((resolve, reject) => {
        element.toBlob(value => (value ? resolve(value) : reject(new Error('The card could not be made'))), 'image/jpeg', 0.92)
    }).then(blob => blob.arrayBuffer()).then(buffer => new Uint8Array(buffer))

    // a pdf of one A4 page (in points) with the jpeg over all of it
    const pageWidth = 595.28
    const pageHeight = 841.89
    const content = `q ${pageWidth} 0 0 ${pageHeight} 0 0 cm /Im0 Do Q`
    const objects = [
        '<< /Type /Catalog /Pages 2 0 R >>',
        '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
        `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>`,
        [`<< /Type /XObject /Subtype /Image /Width ${element.width} /Height ${element.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`, jpeg, '\nendstream'],
        `<< /Length ${content.length} >>\nstream\n${content}\nendstream`
    ]
    const parts = ['%PDF-1.4\n']
    let length = parts[0].length
    const offsets = objects.map((object, index) => {
        const offset = length
        ;[`${index + 1} 0 obj\n`, ...[].concat(object), '\nendobj\n'].forEach(part => {
            parts.push(part)
            length += part.length
        })
        return offset
    })
    const xref = ['xref', `0 ${objects.length + 1}`, '0000000000 65535 f ', ...offsets.map(offset => `${String(offset).padStart(10, '0')} 00000 n `)].join('\n')
    parts.push(`${xref}\ntrailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${length}\n%%EOF`)
    return new Blob(parts, { type: 'application/pdf' })
}

export { toDataUrl }
